import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  AttributionResponse,
  AudienceResponse,
  Campaign,
  CampaignDetail,
  CreateCampaignInput,
  CreateSegmentInput,
  LifecycleTriggersResponse,
  PrimitivesResponse,
  ResolveResult,
  SavedSegment,
  UpdateSegmentInput,
} from "../../api/marketing-contracts";

// MK-FE — the marketing console reads (marketing:read@all) + campaign writes
// (marketing:manage@all). Read-only transparency + governed campaign definition;
// NOTHING here dispatches (send is residency/İYS-deferred). No client-side
// aggregation — every hook returns the server rollup verbatim.

const MK = ["marketing"];

/** GET /v1/marketing/audience — the no-spam ledger (reachable vs suppressed). */
export function useAudience() {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "audience"],
    queryFn: () => api.get<AudienceResponse>("/v1/marketing/audience"),
  });
}

/** GET /v1/marketing/lifecycle-triggers — governed nudge cohorts (MK-3; send OFF). */
export function useLifecycleTriggers() {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "lifecycle-triggers"],
    queryFn: () => api.get<LifecycleTriggersResponse>("/v1/marketing/lifecycle-triggers"),
  });
}

/** GET /v1/marketing/attribution — source → conversion + CAC skeleton. */
export function useAttribution() {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "attribution"],
    queryFn: () => api.get<AttributionResponse>("/v1/marketing/attribution"),
  });
}

/** GET /v1/marketing/campaigns — the org's campaigns. */
export function useCampaigns() {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "campaigns"],
    queryFn: () => api.get<Campaign[]>("/v1/marketing/campaigns"),
  });
}

/** GET /v1/marketing/campaigns/:id — a campaign + its resolved targets. */
export function useCampaign(id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "campaign", id],
    enabled: id !== null,
    queryFn: () => api.get<CampaignDetail>(`/v1/marketing/campaigns/${id}`),
  });
}

/** POST /v1/marketing/campaigns — define a DRAFT campaign; refreshes the list. */
export function useCreateCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      api.post<Campaign>("/v1/marketing/campaigns", input),
    onSettled: () => void qc.invalidateQueries({ queryKey: [...MK, "campaigns"] }),
  });
}

/** POST /v1/marketing/campaigns/:id/resolve — evaluate the audience (writes targets;
 *  DISPATCHES NOTHING). Refreshes the campaign detail + list. */
export function useResolveCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<ResolveResult>(`/v1/marketing/campaigns/${id}/resolve`, {}),
    onSettled: (_data, _err, id) => {
      void qc.invalidateQueries({ queryKey: [...MK, "campaign", id] });
      void qc.invalidateQueries({ queryKey: [...MK, "campaigns"] });
    },
  });
}

/** PATCH /v1/marketing/campaigns/:id — advance status (ACTIVE / CLOSED). */
export function useUpdateCampaign() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; status: "ACTIVE" | "CLOSED" }) =>
      api.patch<Campaign>(`/v1/marketing/campaigns/${input.id}`, { status: input.status }),
    onSettled: (_data, _err, input) => {
      void qc.invalidateQueries({ queryKey: [...MK, "campaign", input.id] });
      void qc.invalidateQueries({ queryKey: [...MK, "campaigns"] });
    },
  });
}

/** One governed system segment as the SERVER defines it (crm/segments.ts). */
export interface SegmentSummary {
  key: string;
  label: string;
  count: number;
}

/**
 * A10 · GET /v1/crm/segments — the canonical system-segment list.
 *
 * The campaign picker used to hold its own copy of the four keys. They happened
 * to match, so nothing was visibly broken — but two failure modes were already
 * live: the option TEXT was the raw kebab key (`renewal-due-30d`) on a
 * Turkish-first surface whose server-side label is "30 günde yenileme", and a
 * segment added server-side would never appear here, silently, with no error to
 * notice. A stale key in the other direction is a dropdown entry that always
 * 400s on create.
 *
 * `party:read@all` is held by ADMIN (seed roles.ts), so this is not a new grant.
 * The counts come back per-book and are ignored here — the picker needs the
 * vocabulary, not the sizes.
 */
export function useSystemSegments() {
  const api = useApi();
  return useQuery({
    queryKey: [...MK, "system-segments"],
    queryFn: () => api.get<SegmentSummary[]>("/v1/crm/segments"),
  });
}

// ── CR-1b saved-segment authoring (#291) — the write surface that consumes
//    POST/PATCH /v1/crm/saved-segments. Reads are party:read@own; writes are
//    segment:write@all (MANAGER/ADMIN). The BE's segment-compiler owns the
//    primitive + param allow-list — a 400 with a precise path on any violation.
const SEG = ["saved-segments"];

/** GET /v1/crm/saved-segments — the org's authored segments. */
export function useSavedSegments() {
  const api = useApi();
  return useQuery({
    queryKey: [...SEG, "list"],
    queryFn: () => api.get<SavedSegment[]>("/v1/crm/saved-segments"),
  });
}

/** GET /v1/crm/saved-segments/primitives — the governed authoring vocabulary (A13). */
export function useSegmentPrimitives() {
  const api = useApi();
  return useQuery({
    queryKey: [...SEG, "primitives"],
    queryFn: () => api.get<PrimitivesResponse>("/v1/crm/saved-segments/primitives"),
  });
}

/** POST /v1/crm/saved-segments — author a segment (governed composition); refreshes the list. */
export function useCreateSegment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSegmentInput) =>
      api.post<SavedSegment>("/v1/crm/saved-segments", input),
    onSettled: () => void qc.invalidateQueries({ queryKey: [...SEG, "list"] }),
  });
}

/** PATCH /v1/crm/saved-segments/:id — rename / re-define / archive; refreshes the list. */
export function useUpdateSegment() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateSegmentInput) => {
      const { id, ...body } = input;
      return api.patch<SavedSegment>(`/v1/crm/saved-segments/${id}`, body);
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: [...SEG, "list"] }),
  });
}
